import { HandArrowDownIcon, PackageIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetPackageSignature } from '@/api/packages/get-package-signature'
import { useGetPackagesSettings } from '@/api/packages/get-packages-settings'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { ImagePreviewModal } from '@/components/ui/image-preview-modal'
import { Modal } from '@/components/ui/modal'
import { Typography } from '@/components/ui/typography'
import { ZoomableImage } from '@/components/ui/zoomable-image'
import { EMPTY_VALUE } from '@/constants'
import { popupFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import { PackageModel, PackageStatusSchema } from '@/models'
import { getPackageBadgeColor, getPackageStatusLabels } from '@/pages/packages'
import { CheckoutPackageModal } from '@/pages/packages/checkout-package-modal/checkout-package-modal'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import {
  formatDateTime,
  formatDeliveryTimeSlot,
  getCarrierName,
  getDeliveryTypeName,
  getResidentUnitDisplay,
} from '@/utils'

import * as S from './package-details-modal.styled'

interface PackageDetailsModalProps {
  onClose: () => void
  packageData: PackageModel
}

export const PackageDetailsModal = ({ onClose, packageData }: PackageDetailsModalProps) => {
  const { t } = useTranslation(['packages'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  const buildingId = selectedBuilding?.id

  const { data: packagesSettings } = useGetPackagesSettings({ buildingId: buildingId ?? 0 })

  const slotLength = packagesSettings?.deliverySlotLength

  const { data: signatureUrl } = useGetPackageSignature({
    packageId: packageData.id,
  })

  const packageImageUrl =
    typeof packageData.image === 'string' && packageData.image
      ? getAssetLink({
          fallback: { file: 'notification_image.png' },
          file: packageData.image,
          path: 'chitown-images/notification',
        })
      : null

  const proofOfDeliveryUrl = packageData.deliveryImage
    ? getAssetLink({
        fallback: { file: 'notification_image.png' },
        file: packageData.deliveryImage,
        path: 'chitown-images/notification',
      })
    : null

  const gallery = [packageImageUrl, proofOfDeliveryUrl, signatureUrl].filter((src): src is string =>
    Boolean(src),
  )

  const getGalleryIndex = (src: string) => Math.max(gallery.indexOf(src), 0)

  const showActions =
    packageData.status === PackageStatusSchema.enum.Pending ||
    packageData.status === PackageStatusSchema.enum.Requested

  const handleCheckout = () => {
    popupFactory.open(CheckoutPackageModal, { selectedPackageData: packageData }, { force: true })
  }

  return (
    <Modal
      autoWidth
      title={t('packages:packageDetailsModal.title')}
      headerIcon={PackageIcon}
      height="74.8rem"
      footer={
        showActions ? (
          <>
            <Button use="secondary" onClick={onClose}>
              {t('packages:packageDetailsModal.close')}
            </Button>
            <Button use="primary" onClick={handleCheckout}>
              <Icon icon={HandArrowDownIcon} />
              {t('packages:packageDetailsModal.checkout')}
            </Button>
          </>
        ) : undefined
      }
      onClose={onClose}
    >
      <S.ModalContent>
        <div>
          <S.SectionHeaderRow>
            <S.SectionHeader>{t('packages:packageDetailsModal.identification')}</S.SectionHeader>
            {packageData.status && (
              <Badge color={getPackageBadgeColor(packageData.status)} variant="light" size="small">
                {getPackageStatusLabels(t)[packageData.status]}
              </Badge>
            )}
          </S.SectionHeaderRow>
          <S.SectionBody>
            <S.Item>
              <span>{t('packages:packageDetailsModal.packageId')}</span>
              <span>{packageData.id}</span>
            </S.Item>
            <S.Item>
              <span>{t('packages:packageDetailsModal.trackingNumber')}</span>
              <span>{packageData.trackingNumber || EMPTY_VALUE}</span>
            </S.Item>
            {packageImageUrl ? (
              <S.ImageRow>
                <span>{t('packages:packageDetailsModal.image')}</span>
                <ZoomableImage
                  fit="contain"
                  height="39rem"
                  width="100%"
                  src={packageImageUrl}
                  images={gallery}
                  initialIndex={getGalleryIndex(packageImageUrl)}
                  alt={t('packages:packageDetailsModal.packagePhotoAlt')}
                  title={t('packages:packageDetailsModal.title')}
                />
              </S.ImageRow>
            ) : (
              <S.Item>
                <span>{t('packages:packageDetailsModal.image')}</span>
                <span>{t('packages:packageDetailsModal.noImage')}</span>
              </S.Item>
            )}
            <S.Item>
              <span>{t('packages:packageDetailsModal.signature')}</span>
              <span>
                {signatureUrl ? (
                  <ZoomableImage
                    fit="contain"
                    height="6rem"
                    width="6rem"
                    src={signatureUrl}
                    images={gallery}
                    initialIndex={getGalleryIndex(signatureUrl)}
                    alt={t('packages:packageDetailsModal.signatureImageAlt')}
                    title={t('packages:packageDetailsModal.title')}
                  />
                ) : (
                  EMPTY_VALUE
                )}
              </span>
            </S.Item>
            <S.Item>
              <div>
                <span>{t('packages:packageDetailsModal.comment')}</span>
                <Typography
                  as="p"
                  color="black800"
                  size="text-small"
                  weight="regular"
                  whiteSpace="pre-wrap"
                >
                  {packageData.comment || EMPTY_VALUE}
                </Typography>
              </div>
            </S.Item>
          </S.SectionBody>
        </div>

        <div>
          <S.SectionHeader>{t('packages:packageDetailsModal.residentInformation')}</S.SectionHeader>
          <S.SectionBody>
            <S.Item>
              <span>{t('packages:packageDetailsModal.residentName')}</span>
              <span>{packageData.residentName}</span>
            </S.Item>
            <S.Item>
              <span>{t('packages:packageDetailsModal.residentUnitNumber')}</span>
              <span>{getResidentUnitDisplay(packageData, selectedBuilding)}</span>
            </S.Item>
          </S.SectionBody>
        </div>

        <div>
          <S.SectionHeader>
            {t('packages:packageDetailsModal.logisticsInformation')}
          </S.SectionHeader>
          <S.SectionBody>
            <S.Item>
              <span>{t('packages:packageDetailsModal.carrier')}</span>
              <span>{getCarrierName(packageData.carrier)}</span>
            </S.Item>
            <S.Item>
              <span>{t('packages:packageDetailsModal.perishable')}</span>
              <span>
                {packageData.perishable ? t('packages:yesNo.yes') : t('packages:yesNo.no')}
              </span>
            </S.Item>
            {packageData.deliveryType && (
              <S.Item>
                <span>{t('packages:packageDetailsModal.deliveryType')}</span>
                <span>{getDeliveryTypeName(packageData.deliveryType)}</span>
              </S.Item>
            )}
            {packageData.checkinAt && (
              <S.Item>
                <span>{t('packages:packageDetailsModal.arrivedAt')}</span>
                <span>{formatDateTime(packageData.checkinAt)}</span>
              </S.Item>
            )}
            {packageData.requestedDate && (
              <S.Item>
                <span>{t('packages:packageDetailsModal.requestedAt')}</span>
                <span>{formatDateTime(packageData.requestedDate)}</span>
              </S.Item>
            )}
            {packageData.checkoutAt && (
              <S.Item>
                <span>{t('packages:packageDetailsModal.deliveredAt')}</span>
                <span>{formatDateTime(packageData.checkoutAt)}</span>
              </S.Item>
            )}
            {packageData.deliveryDate &&
              packageData.deliveryHours &&
              packageData.deliveryHoursEnd &&
              slotLength !== undefined && (
                <S.Item>
                  <span>{t('packages:packageDetailsModal.requestedDeliverySlot')}</span>
                  <span>
                    {formatDeliveryTimeSlot({
                      date: packageData.deliveryDate,
                      deliverySlotLength: slotLength,
                      endMinutes: packageData.deliveryHoursEnd,
                      startMinutes: packageData.deliveryHours,
                    })}
                  </span>
                </S.Item>
              )}
            {packageData.location && (
              <S.Item>
                <span>{t('packages:packageDetailsModal.location')}</span>
                <span>{packageData.location}</span>
              </S.Item>
            )}
            {packageData.deliveryImage && (
              <S.Item>
                <span>{t('packages:packageDetailsModal.proofOfDelivery')}</span>
                <S.ImageLink
                  onClick={() =>
                    proofOfDeliveryUrl &&
                    popupFactory.open(
                      ImagePreviewModal,
                      {
                        images: gallery,
                        initialIndex: getGalleryIndex(proofOfDeliveryUrl),
                        title: t('packages:packageDetailsModal.title'),
                      },
                      { suspendCurrent: true },
                    )
                  }
                >
                  {packageData.deliveryImage}
                </S.ImageLink>
              </S.Item>
            )}
          </S.SectionBody>
        </div>
      </S.ModalContent>
    </Modal>
  )
}
